// Issue a medical certificate, fitness note or referral letter from a consultation
// or from the patient's record.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClinicCtx } from '@/app/state';
import { Button, Field, Input, Textarea, cx } from '@/components/ui';
import { issueDocument, restDays, type DocKind } from './repo';

const KINDS: DocKind[] = ['medical_certificate', 'fitness', 'referral'];
const KIND_LABEL: Record<DocKind, string> = { medical_certificate: 'doc.certificate', fitness: 'doc.fitness', referral: 'doc.referral', other: 'doc.other' };

export function IssueCertificate({ patientId, encounterId, doctorId, diagnosisText, prefix, onIssued, onCancel }: {
  patientId: string; encounterId?: string | null; doctorId?: string | null; diagnosisText?: string | null;
  prefix: string; onIssued: (id: string) => void; onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const ctx = useClinicCtx();
  const today = new Date().toISOString().slice(0, 10);
  const [kind, setKind] = useState<DocKind>('medical_certificate');
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [includeDx, setIncludeDx] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [body, setBody] = useState('');
  const [language, setLanguage] = useState<'my' | 'en' | 'both'>(i18n.language === 'my' ? 'my' : 'en');
  const [busy, setBusy] = useState(false);

  // A fitness note has a single date; a certificate has a range.
  useEffect(() => { if (kind === 'fitness' && to !== from) setTo(from); }, [kind, from, to]);

  const days = kind === 'medical_certificate' ? restDays(from, to) : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const id = await issueDocument(ctx, {
        patient_id: patientId, encounter_id: encounterId ?? null, doctor_id: doctorId ?? null, kind,
        rest_from: kind === 'referral' ? null : from, rest_to: kind === 'medical_certificate' ? to : null,
        include_diagnosis: includeDx, diagnosis_text: diagnosisText ?? null,
        recipient: recipient.trim() || null, body: body.trim() || null, language,
      }, prefix);
      onIssued(id);
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="grid gap-3">
      <Field label={t('doc.kind')}>
        <div className="flex flex-wrap gap-1.5">
          {KINDS.map((k) => (
            <button type="button" key={k} onClick={() => setKind(k)}
              className={cx('rounded-full border px-3 py-1.5 text-sm font-semibold', kind === k ? 'border-accent bg-accent-soft text-accent-ink' : 'border-rule bg-surface text-ink-2')}>
              {t(KIND_LABEL[k])}
            </button>
          ))}
        </div>
      </Field>

      {kind === 'medical_certificate' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Field label={t('doc.from')}><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required /></Field>
          <Field label={t('doc.to')}><Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} required /></Field>
          <div className="mb-3 self-end rounded-lg bg-accent-soft px-3 py-2.5 text-center font-bold text-accent-ink">{days ? t('doc.days', { count: days }) : '—'}</div>
        </div>
      )}
      {kind === 'fitness' && <Field label={t('doc.fitFrom')}><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required /></Field>}

      <Field label={t('doc.recipient')}><Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder={t('doc.recipientPlaceholder')} /></Field>

      {diagnosisText && (
        <label className="flex items-start gap-2">
          <input type="checkbox" className="mt-1 h-5 w-5" checked={includeDx} onChange={(e) => setIncludeDx(e.target.checked)} />
          <span>{t('doc.includeDiagnosis')} <b>{diagnosisText}</b><br /><span className="text-sm text-muted">{t('doc.includeDiagnosisHint')}</span></span>
        </label>
      )}

      <Field label={t('doc.body')}><Textarea className="min-h-16" value={body} onChange={(e) => setBody(e.target.value)} /></Field>

      <Field label={t('doc.language')}>
        <div className="flex gap-1.5">
          {(['my', 'en', 'both'] as const).map((l) => (
            <button type="button" key={l} onClick={() => setLanguage(l)}
              className={cx('rounded-full border px-3 py-1.5 text-sm font-semibold', language === l ? 'border-accent bg-accent-soft text-accent-ink' : 'border-rule bg-surface text-ink-2')}>
              {t(`doc.${l}`)}
            </button>
          ))}
        </div>
      </Field>

      <div className="flex justify-end gap-2">
        <Button type="button" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" variant="primary" disabled={busy}>{t('doc.issue')}</Button>
      </div>
    </form>
  );
}
